import { BaseDataSourceDetails } from "@me8eon/data_sources";
import { SelectableButton, SimpleSelectableButton } from "@me8eon/selection_button";
import { DataView, DataViews, TestOneLineLayout, TestSearchResultLayout } from "@me8eon/data_views";
import "./eon.dataviews.scss";
import React from "react";
import { ThemedIcon } from "@me8eon/icons";
import { useAiAssist } from "@me8eon/ai_assist_data_widget";
import { Setter } from "@me8eon/context";
import { FeatureFlag, FeatureFlags } from "@me8eon/featureflags";
import { AllSearchTypes } from "@me8eon/all_search";
import { elasticSearch, elasticSearchAggregates, elasticSearchKnn } from "@me8eon/elastic_search_data_sources";
import { graphApiEmails, graphApiFiles, graphApiPeople, graphApiTeams, graphSearchAggregates } from "@me8eon/graph_api_data_sources";
import { aggregationsSearch, generateFilterFromData, immediateSearch, submitSearch } from "@me8eon/search";
import { mimeTypeFilterPluginName } from "@me8eon/mimetype_filter";
import { EnvironmentConfig } from "@me8eon/config";
import { AllDataViewSearchResultLayout } from "./allDataViewSearchResultLayout";
import { isCompassFeatureFlagEnabled } from "@me8eon/compass_data_plugin";
import { intentSearchName } from "@me8eon/react_intent";

export const TestDataViewCommon = {
    plugin: "dataview" as const,
    DisplayNavItem: SimpleSelectableButton,
    SearchResultLayout: TestSearchResultLayout,
    OneLineLayout: TestOneLineLayout,
    changeView: {
        forceSearchWhenSelectView: true,
        cleanWhenSelectView: true,
    },
};

const officeBuddyIndexName = EnvironmentConfig.eonKnowledgeBaseIndex;
const compassIndexName = EnvironmentConfig.compassIndex;
const kbArticlesIndexName = EnvironmentConfig.itSupportIndex;
export const immediateSearchIndiciesBase = [
    EnvironmentConfig.semanticOfficeBuddyIndex,
    EnvironmentConfig.appIndex, "jira-prod", "confluence-prod", "gitlab-prod", "mygenius-api", "order-it-api",
    EnvironmentConfig.eonKnowledgeBaseIndex
];


export const allDataView = (featureFlags: FeatureFlags, featureFlagEnabled: any, empType: string): DataView<AllSearchTypes, BaseDataSourceDetails> => {
        //legacy scaffolding
        function index(ff: FeatureFlag): string[] {
            const searchIndicies = ["jira-prod", "confluence-prod", "gitlab-prod", "mygenius-api"];
            const searchIndiciesWithOrderIt = [...searchIndicies, "order-it-api"];
            const searchIndiciesWithTestForVasu = [...searchIndicies, "order-it-api-test"];
            if (!ff) return searchIndicies; //no feature flag: default
            if (ff.value === "default") return searchIndicies;
            if (ff.value === "orderIt") return searchIndiciesWithOrderIt;
            if (ff.value === "testForVasu") return searchIndiciesWithTestForVasu;
            throw new Error(`Unknown value ${ff.value}`);
        }

        let indicies = index({value: "orderIt"} as any); //default to having order it in indicies since that's the current state of prod
        let submitSearchIndicies = ["jira-prod(key,status,priority)", "confluence-prod(space)", "gitlab-prod", "mygenius-api", "order-it-api", EnvironmentConfig.eonKnowledgeBaseIndex];
        let immediateSearchIndicies = [...immediateSearchIndiciesBase];
        //

        if (isCompassFeatureFlagEnabled(empType)) {
            indicies.push(compassIndexName);
            immediateSearchIndicies.push(compassIndexName);
            submitSearchIndicies.push(compassIndexName);
        }

        if (featureFlagEnabled) {
            indicies.push(...kbArticlesIndexName);
            immediateSearchIndicies.push(...kbArticlesIndexName);
            submitSearchIndicies.push(...kbArticlesIndexName);
        }

        if (featureFlagEnabled) {
            indicies.push(officeBuddyIndexName);
            immediateSearchIndicies.push(officeBuddyIndexName);
            submitSearchIndicies.push(officeBuddyIndexName);
        }

        return ({
            ...TestDataViewCommon,
            //legacy
            needsOffice: true,
            searchIndices: indicies,
            //
            search: {
                [immediateSearch]: {
                    dataSource: [
                        {
                            type: elasticSearch, names: immediateSearchIndicies,
                        }],
                },
                [submitSearch]: {
                    dataSource: [
                        { type: graphApiFiles, names: ["files"] },
                        { type: elasticSearch, names: submitSearchIndicies },
                    ],
                },
                [intentSearchName]: {
                    dataSource: [],
                },
            },

            icon: <ThemedIcon name={"all"} />,
            SearchResultLayout: AllDataViewSearchResultLayout,
            widgets: ["restOfElasticSearch", { widget: "sharepointWithMore", width: "medium" }],
        });
    }
;


export const sharepointDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = {
    //legacy
    needsOffice: true,
    searchIndices: ["m365"],
    //
    icon: <ThemedIcon name={"myFiles"} />,
    ...TestDataViewCommon,
    search: {
        [immediateSearch]: {
            dataSource: [{ type: graphApiFiles, names: ["files"] }],
        },
        [submitSearch]: {
            dataSource: [{ type: graphApiFiles, names: ["files"] }],
        },
    },
    widgets: [{ widget: "sharepoint", width: "large" }, { widget: `filter(${mimeTypeFilterPluginName})`, width: "small" }],
};


export const appsDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = {
    //legacy
    searchIndices: [EnvironmentConfig.appIndex],
    needsOffice: false,
    //
    ...TestDataViewCommon,
    icon: <ThemedIcon name={"app"} />,
    search: {
        [immediateSearch]: {
            dataSource: [{ type: elasticSearch, names: [EnvironmentConfig.appIndex] }],
        },
        [submitSearch]: {
            dataSource: [{ type: elasticSearch, names: [EnvironmentConfig.appIndex] }],
        },
    },
    widgets: ["apps"],
};
export const peopleDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = {
    //legacy
    searchIndices: ["people"],
    needsOffice: true,
    //
    ...TestDataViewCommon,
    search: {
        [submitSearch]: {
            dataSource: [{ type: graphApiPeople, names: ["people"] }],
        },
        [immediateSearch]: {
            dataSource: [{ type: graphApiPeople, names: ["people"] }],
        },
        [generateFilterFromData]: {
            dataSource: [{ type: graphSearchAggregates, names: ["people(departmentType)"] }],
        },
    },
    icon: <ThemedIcon name={"user_group"} />,
    // widgets: [{ widget: "people", width: "large" }, { widget: `filter(${departmentTypeFilterPluginName})`, width: "small" }],
    widgets: ["people"],
};

export const emailsDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = {
    //legacy
    searchIndices: ["emails"],
    needsOffice: true,
    //
    ...TestDataViewCommon,
    search: {
        [submitSearch]: {
            dataSource: [{ type: graphApiEmails }],
        },
        [immediateSearch]: {
            dataSource: [{ type: graphApiEmails }],
        },
    },
    icon: <ThemedIcon name={"outlook"} />,
    widgets: ["emails"],
};


export const teamsDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = {
    //legacy
    searchIndices: ["emails"],
    needsOffice: true,
    //
    ...TestDataViewCommon,
    search: {
        [submitSearch]: {
            dataSource: [{ type: graphApiTeams, names: [] }],
        },
        [immediateSearch]: {
            dataSource: [{ type: graphApiTeams, names: [] }],
        },
    },
    icon: <ThemedIcon name={"teams"} />,
    widgets: ["teams"],
};
export const jiraDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = ({
    //legacy
    needsOffice: false,
    searchIndices: ["jira-prod"],
    //
    ...TestDataViewCommon,
    search: {
        [immediateSearch]: {
            dataSource: [{ type: elasticSearch, names: ["jira-prod"] }],
        },
        [submitSearch]: {
            dataSource: [{ type: elasticSearch, names: ["jira-prod(key,priority,status)"] }],
        },
        [aggregationsSearch]: { dataSource: [{ type: elasticSearchAggregates, names: ["jira-prod(key,priority,status)"] }] },
    },
    icon: <ThemedIcon name={"jira"} shouldChangeColor={false} />,
    widgets: [{ widget: "jira", width: "large" }, { widget: "filter(time, agg:key, agg:priority, agg:status)", width: "small" }],//what we display
});

export const confluenceDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = ({
    //legacy
    needsOffice: false,
    searchIndices: ["confluence-prod"],
    //
    ...TestDataViewCommon,
    icon: <ThemedIcon name={"confluence"} shouldChangeColor={false} />,
    search: {
        [immediateSearch]: {
            dataSource: [{ type: elasticSearch, names: ["confluence-prod"] }],
        },
        [submitSearch]: {
            dataSource: [{ type: elasticSearch, names: ["confluence-prod(space)"] }],
        },
        //probably shouldn't use default but use the above from search
        [aggregationsSearch]: { dataSource: [{ type: elasticSearchAggregates, names: ["confluence-prod(space)"] }] },

    },
    widgets: [{ widget: "confluence", width: "large" }, { widget: "filter(agg:space)", width: "small" }],
});

export const gitlabDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = {
    //legacy
    needsOffice: false,
    searchIndices: ["gitlab-prod"],
    //
    ...TestDataViewCommon,
    icon: <ThemedIcon name="gitlab" />,
    search: {
        [immediateSearch]: {
            dataSource: [{ type: elasticSearch, names: ["gitlab-prod"] }],
        },
        [submitSearch]: {
            dataSource: [{ type: elasticSearch, names: ["gitlab-prod"] }],
        },
    },
    widgets: ["gitlab"],
};

export const myGeniusDataView = (ff: FeatureFlags): DataView<AllSearchTypes, BaseDataSourceDetails> => ({
    //legacy
    needsOffice: false,
    searchIndices: ["mygenius-api"],
    //
    ...TestDataViewCommon,
    icon: <ThemedIcon name={"mygenius"} />,
    search: {
        [immediateSearch]: {
            dataSource: [{ type: elasticSearch, names: ["mygenius-api"] }],
        },
        [submitSearch]: {
            dataSource: [{ type: elasticSearch, names: ["mygenius-api(card_languages,ecl_source_name,standard_card_type)"] }],
        },
        [aggregationsSearch]: {
            dataSource: [{ type: elasticSearchAggregates, names: ["mygenius-api(card_languages,ecl_source_name,standard_card_type)"] }],
        },
    },
    widgets: [{ widget: "mygenius", width: "large" }, { widget: "filter(agg:card_languages, agg:ecl_source_name, agg:standard_card_type)", width: "small" }],
});

export const compassDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = {
    //legacy
    needsOffice: false,
    searchIndices: [compassIndexName],
    //
    ...TestDataViewCommon,
    icon: <ThemedIcon name={"compass"} shouldChangeColor={false} />,
    search: {
        [immediateSearch]: {
            dataSource: [{ type: elasticSearch, names: [compassIndexName] }],
        },
        [submitSearch]: {
            dataSource: [{ type: elasticSearch, names: [`${compassIndexName}(companyTags, categoryTags)`] }],
        },
        [aggregationsSearch]: {
            dataSource: [{ type: elasticSearchAggregates, names: [`${compassIndexName}(companyTags, categoryTags)`] }],
        },
    },
    widgets: [{ widget: "compass", width: "large" }, { widget: "filter(company, agg:companyTags, radio:categoryTags)", width: "small" }],
    default: { company: "local", categoryTags: "All Content" },
    size: "small",
    conditionToHide: { companyTags: (guiFilters: Record<string, string>) => guiFilters?.company !== "compassWide" },
    filterSortOrder: {
        categoryTags: [
            "All Content",
            "Sites and Pages",
            "Official News",
            "Knowledge Bases",
            "Knowledge Articles",
            "Compass Documents",
            "Events",
            "FAQs",
            "Locations",
            "Communities",
            "Community Content"
        ]
    }
};

export const kbArticlesDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = {
    //legacy
    needsOffice: false,
    searchIndices: [...kbArticlesIndexName],
    //
    ...TestDataViewCommon,
    icon: <ThemedIcon name={"itSupport"} />,
    search: {
        [immediateSearch]: {
            dataSource: [{ type: elasticSearch, names: [...kbArticlesIndexName] }],
        },
        [submitSearch]: {
            dataSource: [{ type: elasticSearch, names: [...kbArticlesIndexName] }],
        },
        [aggregationsSearch]: {
            dataSource: [{ type: elasticSearchAggregates, names: [...kbArticlesIndexName] }],
        },
    },
    widgets: ["kb_articles"],
};

export const officeBuddyDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = {
    //legacy
    needsOffice: false,
    searchIndices: [officeBuddyIndexName],
    //
    ...TestDataViewCommon,
    icon: <ThemedIcon name={"knowledgeBase"} />,
    search: {
        [immediateSearch]: {
            dataSource: [{ type: elasticSearch, names: [officeBuddyIndexName] }],
        },
        [submitSearch]: {
            dataSource: [{ type: elasticSearch, names: [officeBuddyIndexName] }],
        },
    },
    widgets: ["officeBuddy"],
};

const AiAssistantButton: SelectableButton = (props) => {
    const [aiAssist] = useAiAssist();
    const fakeValue = "whocares";
    const setFakeValue: Setter<string> = () => aiAssist.setAiAssistNavbarClicked?.(true);
    return <SimpleSelectableButton {...props} selectedOps={[fakeValue, setFakeValue]} />;
};

export const aiAssistantDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = {
    //legacy
    needsOffice: false,
    searchIndices: [],
    //
    ...TestDataViewCommon,
    changeView: {
        forceSearchWhenSelectView: false,
        cleanWhenSelectView: true,
    },
    DisplayNavItem: AiAssistantButton,
    icon: <ThemedIcon name="AiIcon24" />,
    search: {
        aiAssistant: {
            dataSource: [{ type: elasticSearchKnn, names: ["nextthink", "ai-assist", "it-order"] }],
        },
    },
    widgets: [],
};

export const itOrderDataView: DataView<AllSearchTypes, BaseDataSourceDetails> = {
    //legacy
    needsOffice: false,
    searchIndices: ["order-it-api"],
    //
    ...TestDataViewCommon,
    icon: <ThemedIcon name="cart" />,
    search: {
        [immediateSearch]: {
            dataSource: [{ type: elasticSearch, names: ["order-it-api"] }],
        },
        [submitSearch]: {
            dataSource: [{ type: elasticSearch, names: ["order-it-api"] }],
        },
    },
    widgets: ["orderit"],
};

export const allDataViews = (featureFlags: FeatureFlags, featureFlagEnabled: boolean, empType: string): DataViews<AllSearchTypes, BaseDataSourceDetails> => ({
    all: allDataView(featureFlags, featureFlagEnabled, empType),
    apps: appsDataView,
    people: peopleDataView,
    ...(isCompassFeatureFlagEnabled(empType) && { compass: compassDataView }),
    mygenius: myGeniusDataView(featureFlags),
    m365: sharepointDataView,
    emails: emailsDataView,
    orderit: itOrderDataView,
    jira: jiraDataView,
    confluence: confluenceDataView,
    // teams: teamsDataView,
    gitlab: gitlabDataView,
    ...(featureFlagEnabled && { kb_articles: kbArticlesDataView }),
    officeBuddy: officeBuddyDataView,
    aiAssistant: aiAssistantDataView,
});

